/**
 * Live trivia detail / pregame lobby page.
 *
 * Sits between the discovery list and the actual game room at
 * /live/{id}/play. Shows the title, question count, prize info, an
 * inline eraser opt-in (when the quiz allows them), the action button,
 * and (for scheduled trivias) a live countdown.
 *
 * Polling: re-fetches /live/{id}/info every 5s so when the trivia
 * transitions from "scheduled" to "live" the CTA flips to
 * "Enter game →" without the user having to refresh. The 1s countdown
 * tick is a separate, cheaper local timer that only mutates the
 * countdown DOM node.
 *
 * Auto-redirects on finished/cancelled — there's nothing for the user
 * to do here once results exist. Stays put in every other state so
 * the user can read the briefing and adjust their eraser pick.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const POLL_MS = 5000;

// sessionStorage-backed set of live_ids the user has been auto-navigated
// out of on this tab. Cleared when the tab closes so the next session
// gets the auto-nav again. Prevents the /play ↔ /live/{id} ping-pong.
const AUTO_ENTERED_KEY = 'live_detail_auto_entered_v1';

function _autoEnteredSet() {
  try {
    const raw = sessionStorage.getItem(AUTO_ENTERED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}
function _hasAutoEntered(liveId) {
  return _autoEnteredSet().has(liveId);
}
function _markAutoEntered(liveId) {
  const s = _autoEnteredSet();
  s.add(liveId);
  try {
    sessionStorage.setItem(AUTO_ENTERED_KEY, JSON.stringify([...s]));
  } catch { /* private mode or quota — safe to no-op */ }
}
const TICK_MS = 1000;

export async function renderLiveDetail(root) {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const liveId = parts[1];

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back to home"
         style="display:inline-flex;align-items:center;gap:4px;text-decoration:none;font-size:13px;font-weight:600;color:#fff;">
        ‹ Home
      </a>
      <div class="appbar-title" id="ld-title">Live trivia</div>
    </div>
    <div class="pad" id="ld-body">
      <div class="skel"></div>
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  // Cancel timers when the user navigates away — otherwise we'd keep
  // polling the page from the background forever.
  const ctx = { liveId, pollTimer: null, tickTimer: null, adTickTimer: null, abandoned: false };
  const cleanup = () => {
    ctx.abandoned = true;
    if (ctx.pollTimer) clearTimeout(ctx.pollTimer);
    if (ctx.tickTimer) clearInterval(ctx.tickTimer);
    if (ctx.adTickTimer) clearInterval(ctx.adTickTimer);
  };
  window.addEventListener('popstate', cleanup, { once: true });

  await loadAndRender(ctx);
  if (!ctx.abandoned) startCountdownTick(ctx);
}

async function loadAndRender(ctx) {
  let live;
  let me;
  try {
    [live, me] = await Promise.all([
      api(`/live/${ctx.liveId}/info`),
      api('/me').then((r) => { auth.setMe(r); return r; }).catch(() => auth.getMe()),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    const body = document.getElementById('ld-body');
    if (body) {
      body.innerHTML =
        `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Could not load trivia.')}</div>`;
    }
    return;
  }

  // Finished / cancelled trivias have nothing actionable on this page.
  if (live.status === 'finished' || live.status === 'cancelled') {
    navigate(`/live/${ctx.liveId}/results`);
    return;
  }

  // Already-in players who come back to /live/{id} while the trivia is
  // running shouldn't have to press "Enter game →" to see the current
  // question. Bounce them straight to the play page on first render.
  // sessionStorage-guarded so a manual back-navigation from /play to
  // /live/{id} isn't ping-ponged back — a user tapping ‹ from /play
  // wants to see the trivia info, not get thrown back into the game.
  if (
    live.is_registered
    && live.status === 'live'
    && !_hasAutoEntered(ctx.liveId)
  ) {
    _markAutoEntered(ctx.liveId);
    navigate(`/live/${ctx.liveId}/play`);
    return;
  }

  ctx.live = live;
  ctx.me = me;

  const balance = Number(me?.eraser_balance || 0);
  const quizMax = Number(live.max_erasers_per_player || 0);
  // Preserve toggle state across re-renders if the user already touched it.
  if (!ctx.eraser) {
    ctx.eraser = {
      erasersOn: balance > 0 && quizMax > 0,
      erasersCount: Math.min(quizMax, balance) >= 1 ? 1 : 0,
    };
  }

  // Pre-roll ad gate. `needed` freezes at first render — an admin can't
  // strip the ad mid-session to bypass the countdown. Already-joined
  // users, and users whose live has no ad configured, sail through.
  if (!ctx.ad) {
    const hasAd = !!(live.ad_video_url && String(live.ad_video_url).trim());
    ctx.ad = {
      needed: hasAd && !live.is_registered,
      completed: false,
      threshold: Math.max(0, Number(live.ad_skippable_after_s || 0)),
      startReported: false,
    };
  }

  document.getElementById('ld-title').textContent = live.title;
  const body = document.getElementById('ld-body');
  if (!body) return;
  // Clear any pending ad countdown timer from a previous render — the
  // component either got unmounted (ad completed) or is about to be
  // re-wired fresh with a new setInterval reference.
  if (ctx.adTickTimer) { clearInterval(ctx.adTickTimer); ctx.adTickTimer = null; }
  body.innerHTML = renderBody({ live, me, state: ctx.eraser, ad: ctx.ad });
  wireToggle(ctx.eraser, { live, me });
  wireAdPlayer(ctx);
  wireJoinButton(ctx);

  // Schedule the next poll. We keep polling even after a successful join
  // because the trivia can transition scheduled → live, and we want the
  // CTA to flip without a manual refresh.
  //
  // BUT skip polling while the pre-roll ad is playing — every re-render
  // wipes body.innerHTML, which destroys the <video>/iframe element and
  // resets the Skip countdown, so the player would restart every POLL_MS.
  // The ad's finish() handler calls loadAndRender itself, so polling
  // resumes naturally the moment the ad completes.
  const adInProgress = ctx.ad && ctx.ad.needed && !ctx.ad.completed;
  if (!ctx.abandoned && !adInProgress) {
    ctx.pollTimer = setTimeout(() => { void loadAndRender(ctx); }, POLL_MS);
  }
}

function renderBody({ live, me, state, ad }) {
  const balance = Number(me?.eraser_balance || 0);
  const quizMax = Number(live.max_erasers_per_player || 0);
  const cta = ctaForState(live, ad);
  const adCard = ad && ad.needed && !ad.completed ? renderAdCard(live, ad) : '';

  const tags = `
    <div class="row" style="gap:8px;margin:14px 0;">
      <span class="tag" style="background:rgba(91,44,145,0.25);color:#B8A0E0;font-weight:700;letter-spacing:0.06em;">GROUP</span>
      <span class="tag" style="background:rgba(43,179,155,0.25);color:#5FDDC4;font-weight:700;letter-spacing:0.06em;">SKILL-BASED</span>
    </div>
  `;

  const stats = `
    <div class="card" style="padding:16px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;">
      ${statCol('Entry', live.entry_fee_kobo > 0 ? formatKobo(live.entry_fee_kobo) : 'Free')}
      ${statCol('Prize pool', live.prize_kobo > 0 ? formatKobo(live.prize_kobo) : '—')}
      ${statCol('Paid out', `Top ${Number(live.payout_top_n || 1)}`)}
    </div>
  `;

  const countdown = countdownHtml(live);
  const eraserCard = quizMax > 0 ? renderEraserCard({ quizMax, balance, state }) : '';
  // Subtle warm-up nudge: pre-select the trivia's first category on
  // /play/solo so the player can knock out a free practice round on
  // the same topic before committing to the paid live game. Trivias
  // that ship without a resolvable category (very rare — admin has to
  // configure the game with none) fall through to no card so we don't
  // land the user on Solo with a blank category picker.
  const warmupCard = renderWarmupCard(live);

  return `
    <div class="hero violet" style="padding:22px 18px;background:linear-gradient(180deg,#3A2670 0%,#221547 100%);">
      <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:700;line-height:1.25;">
        ${escape(live.title)}
      </div>
      <div class="muted" style="font-size:13px;margin-top:6px;">
        ${live.question_count} questions${live.answer_window_ms ? ` · ${Math.round((live.answer_window_ms || 0) / 1000)}s each` : ''}
      </div>
      ${countdown}
    </div>

    ${tags}
    ${stats}

    ${adCard}
    ${warmupCard}
    ${eraserCard}

    <button type="button" id="ld-join-btn"
            class="${cta.cls}"
            style="width:100%;margin-top:18px;padding:14px;font-family:var(--font-display);font-size:15px;font-weight:700;"
            ${cta.disabled ? 'disabled' : ''}>
      ${cta.label}
    </button>

    <a href="/policy/anti-cheat" data-link
       style="display:flex;align-items:center;justify-content:center;gap:6px;margin-top:14px;padding:10px;text-decoration:none;color:var(--mute);font-size:11.5px;line-height:1.4;">
      <span style="font-size:14px;line-height:1;">🛡️</span>
      <span>
        <b style="color:#5EE0C6;">Anti-cheat terms &amp; conditions</b>
        · Our effort to reward excellence, not cheats.
        <span style="color:#5EE0C6;text-decoration:underline;">Know more →</span>
      </span>
    </a>
  `;
}

function renderWarmupCard(live) {
  const first = (live.categories || [])[0];
  if (!first || !first.id) return '';
  const name = escape(first.name || 'the topic');
  // Slot the topic id into the query string so /play/solo lands with
  // the picker already scoped to the trivia's category. Falls back to
  // the standard random pick on the Solo page if the id doesn't
  // resolve (e.g. category was renamed between navigation and load).
  const href = `/play/solo?category_id=${encodeURIComponent(first.id)}`;
  return `
    <a href="${href}" data-link
       class="card"
       style="display:block;padding:12px 14px;margin-top:14px;text-decoration:none;color:inherit;background:rgba(43,179,155,0.08);border:1px dashed rgba(95,224,190,0.55);">
      <div class="row" style="align-items:center;gap:12px;">
        <div style="width:36px;height:36px;border-radius:10px;background:rgba(95,224,190,0.16);border:1px solid rgba(95,224,190,0.55);display:flex;align-items:center;justify-content:center;color:#5FE0BE;font-size:14px;flex-shrink:0;">
          ▶
        </div>
        <div class="grow" style="min-width:0;">
          <div style="color:#fff;font-weight:700;font-size:13px;line-height:1.35;">
            Warm up first? Practise <b>${name}</b> in Solo
          </div>
          <div class="muted" style="font-size:11.5px;line-height:1.4;margin-top:2px;">
            Free · no stakes · doesn't affect your ranking
          </div>
        </div>
        <span aria-hidden="true" style="color:#5FE0BE;font-size:16px;flex-shrink:0;">→</span>
      </div>
    </a>
  `;
}

function countdownHtml(live) {
  // Show the countdown for any trivia that has a scheduled start in the
  // future. Once the trivia is live we drop the countdown and let the
  // CTA carry the "go play now" signal.
  const targetIso = live.scheduled_at || live.started_at;
  if (!targetIso) return '';
  const targetMs = new Date(targetIso).getTime();
  if (!Number.isFinite(targetMs)) return '';
  if (live.status === 'live') return '';
  const remain = Math.max(0, targetMs - Date.now());
  return `
    <div style="display:inline-flex;align-items:baseline;gap:8px;margin-top:12px;padding:6px 12px;background:rgba(0,0,0,0.25);border:1px solid rgba(255,255,255,0.10);border-radius:8px;">
      <span style="font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;opacity:0.85;">
        Starts in
      </span>
      <span id="ld-countdown" data-target="${escape(targetIso)}"
            style="font-family:var(--font-display);font-size:16px;font-weight:700;color:#fff;">
        ${fmtRemaining(remain)}
      </span>
    </div>
  `;
}

function fmtRemaining(ms) {
  if (ms <= 0) return 'starting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

function startCountdownTick(ctx) {
  ctx.tickTimer = setInterval(() => {
    const el = document.getElementById('ld-countdown');
    if (!el) return; // not currently rendered (already live)
    const targetIso = el.dataset.target;
    if (!targetIso) return;
    const remain = Math.max(0, new Date(targetIso).getTime() - Date.now());
    el.textContent = fmtRemaining(remain);
    // When the countdown reaches zero, trigger an immediate re-fetch so
    // the CTA picks up the scheduled → live transition without waiting
    // for the next POLL_MS tick. Same ad-in-progress guard as the poll
    // loop — don't wipe the <video> element mid-playback.
    const adInProgress = ctx.ad && ctx.ad.needed && !ctx.ad.completed;
    if (remain === 0 && !adInProgress) {
      if (ctx.pollTimer) clearTimeout(ctx.pollTimer);
      void loadAndRender(ctx);
    }
  }, TICK_MS);
}

function statCol(label, value) {
  return `
    <div style="text-align:center;">
      <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.1;">
        ${escape(value)}
      </div>
      <div class="muted" style="font-size:10.5px;text-transform:uppercase;letter-spacing:0.08em;margin-top:4px;">
        ${escape(label)}
      </div>
    </div>
  `;
}

function renderEraserCard({ quizMax, balance, state }) {
  const canUse = Math.min(quizMax, balance);
  const subtitle = balance > 0
    ? `Auto-corrects your first wrong answer · You have <b style="color:#fff;">${balance}</b>`
    : `Auto-corrects your first wrong answer · <span style="color:var(--pink-t);">You have 0</span>`;
  const startOn = state.erasersOn;
  const disabled = balance === 0;

  const stepperRow = quizMax > 1
    ? `<div id="ld-stepper-row" class="row" style="margin-top:12px;padding:10px 12px;background:#1A1432;border:1px solid #2A2244;border-radius:10px;align-items:center;gap:10px;${startOn ? '' : 'opacity:0.45;pointer-events:none;'}">
         <span class="muted" style="font-size:12px;">How many?</span>
         <div class="sp"></div>
         <button type="button" id="ld-minus" class="button-secondary button-inline" style="padding:2px 10px;font-size:14px;font-weight:700;">−</button>
         <span id="ld-count" style="font-family:var(--font-display);font-size:16px;color:#fff;font-weight:700;min-width:18px;text-align:center;">${state.erasersCount}</span>
         <button type="button" id="ld-plus" class="button-secondary button-inline" style="padding:2px 10px;font-size:14px;font-weight:700;">+</button>
         <span class="muted" style="font-size:11px;">/ ${quizMax}</span>
       </div>`
    : '';

  return `
    <div class="card" style="padding:16px;margin-top:14px;background:rgba(229,193,0,0.06);border:1px solid rgba(229,193,0,0.25);">
      <div class="row" style="gap:12px;align-items:flex-start;">
        <div style="width:42px;height:42px;flex-shrink:0;background:#3a2a14;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:22px;">
          ✏️
        </div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:15px;color:#fff;font-weight:700;font-family:var(--font-display);">
            Use an eraser this game
          </div>
          <div class="muted" style="font-size:12px;line-height:1.45;margin-top:3px;">
            ${subtitle}
          </div>
        </div>
        <button type="button" id="ld-toggle"
                role="switch"
                aria-checked="${startOn ? 'true' : 'false'}"
                ${disabled ? 'disabled' : ''}
                style="
                  flex-shrink:0;width:46px;height:26px;
                  border-radius:999px;border:none;cursor:pointer;
                  background:${startOn ? '#E5C100' : '#2A2244'};
                  position:relative;transition:background 0.15s ease;
                  ${disabled ? 'opacity:0.5;cursor:not-allowed;' : ''}
                ">
          <span id="ld-knob" style="
            position:absolute;top:3px;left:${startOn ? '23px' : '3px'};
            width:20px;height:20px;border-radius:50%;background:#0E0B1F;
            transition:left 0.15s ease;
          "></span>
        </button>
      </div>

      ${stepperRow}

      <div style="margin-top:12px;font-size:12.5px;">
        <span class="muted">${balance === 0 ? 'No erasers yet.' : 'Out of erasers?'}</span>
        <a href="/shop" data-link style="color:#E5C100;font-weight:700;margin-left:4px;text-decoration:none;">
          Buy more →
        </a>
      </div>
    </div>
  `;
}

function ctaForState(live, ad) {
  const isPaid = (live.entry_fee_kobo || 0) > 0;
  if (live.is_registered) {
    if (live.status === 'live') {
      // Trivia is live and we're in — give the user a working "Enter
      // game" button. Tap navigates to /live/{id}/play.
      return { kind: 'enter', cls: 'btn gold', label: 'Enter game →', disabled: false };
    }
    return { kind: 'joined', cls: 'btn', label: 'Joined ✓ · See you at start', disabled: true };
  }
  if (live.is_locked && live.status === 'live') {
    return { kind: 'locked', cls: 'btn', label: 'Started — joining closed', disabled: true };
  }
  // Pre-roll ad still pending — lock the CTA until the player has watched
  // through the skippable threshold. The ad-player component flips
  // ad.completed to true and re-renders once the player unlocks it.
  if (ad && ad.needed && !ad.completed) {
    return { kind: 'ad_gate', cls: 'btn', label: 'Watch the ad to continue', disabled: true };
  }
  const label = isPaid ? `Join for ${formatKobo(live.entry_fee_kobo)}` : 'Join — free';
  return { kind: 'join', cls: 'btn gold', label, disabled: false };
}

// ---- wiring ---------------------------------------------------------------

function wireToggle(state, { live, me }) {
  const balance = Number(me?.eraser_balance || 0);
  const quizMax = Number(live.max_erasers_per_player || 0);
  const canUse = Math.min(quizMax, balance);
  const toggleBtn = document.getElementById('ld-toggle');
  const knob = document.getElementById('ld-knob');
  const stepperWrap = document.getElementById('ld-stepper-row');
  const countEl = document.getElementById('ld-count');
  const minusBtn = document.getElementById('ld-minus');
  const plusBtn = document.getElementById('ld-plus');

  if (!toggleBtn) return;

  const paintToggle = () => {
    toggleBtn.style.background = state.erasersOn ? '#E5C100' : '#2A2244';
    knob.style.left = state.erasersOn ? '23px' : '3px';
    toggleBtn.setAttribute('aria-checked', state.erasersOn ? 'true' : 'false');
    if (stepperWrap) {
      stepperWrap.style.opacity = state.erasersOn ? '1' : '0.45';
      stepperWrap.style.pointerEvents = state.erasersOn ? 'auto' : 'none';
    }
  };
  const paintCount = () => {
    if (countEl) countEl.textContent = String(state.erasersCount);
    if (minusBtn) minusBtn.disabled = state.erasersCount <= 1;
    if (plusBtn) plusBtn.disabled = state.erasersCount >= canUse;
  };

  paintCount();
  toggleBtn.addEventListener('click', () => {
    if (balance === 0) return;
    state.erasersOn = !state.erasersOn;
    if (state.erasersOn && state.erasersCount === 0) state.erasersCount = 1;
    paintToggle();
    paintCount();
  });
  minusBtn?.addEventListener('click', () => {
    if (state.erasersCount > 1) { state.erasersCount -= 1; paintCount(); }
  });
  plusBtn?.addEventListener('click', () => {
    if (state.erasersCount < canUse) { state.erasersCount += 1; paintCount(); }
  });
}

function wireJoinButton(ctx) {
  const btn = document.getElementById('ld-join-btn');
  if (!btn || btn.disabled) return;
  btn.addEventListener('click', async () => {
    // Recompute on every click so the post-join state flip is honoured —
    // capturing once would leave the handler stuck on the original CTA.
    const cta = ctaForState(ctx.live, ctx.ad);
    if (cta.kind === 'enter') {
      navigate(`/live/${ctx.liveId}/play`);
      return;
    }
    if (cta.kind === 'joined') {
      return;
    }
    if (cta.kind === 'ad_gate') {
      return;
    }

    // Paid trivia: confirm the deduction with the user before posting. We
    // use a plain confirm() instead of a styled modal so this can ship on
    // the same release as the backend change without a new component, and
    // it still gives the user a hard "yes I want to be charged" gate.
    const feeKobo = Number(ctx.live.entry_fee_kobo || 0);
    if (feeKobo > 0) {
      const fee = formatKobo(feeKobo);
      const ok = window.confirm(
        `Joining this trivia will deduct ${fee} from your wallet. Continue?`,
      );
      if (!ok) return;
    }

    // Actual first-time join.
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Joining…';
    try {
      const erasers_to_bring = ctx.eraser.erasersOn ? ctx.eraser.erasersCount : 0;
      await api(`/live/${ctx.liveId}/join`, {
        method: 'POST',
        body: { erasers_to_bring },
      });
      try {
        const me = await api('/me');
        auth.setMe(me);
        ctx.me = me;
      } catch { /* non-fatal */ }
      ctx.live.is_registered = true;
      toast.success(
        erasers_to_bring > 0
          ? `Joined with ${erasers_to_bring} eraser${erasers_to_bring === 1 ? '' : 's'}.`
          : 'Joined.',
      );
      // If the trivia is already running, drop the user straight into
      // the game room instead of leaving them on the "Enter game →"
      // second-click screen — that extra tap was the reported bug where
      // users said the Join button didn't take them into the trivia.
      if (ctx.live.status === 'live') {
        _markAutoEntered(ctx.liveId);
        navigate(`/live/${ctx.liveId}/play`);
        return;
      }
      // Still in pregame — re-render so the CTA flips to "Joined ✓"
      // and the countdown continues.
      void loadAndRender(ctx);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = original;
      // Friendly copy for the specific paid-entry failure mode — generic
      // err.message would just say "Not enough wallet balance for the
      // entry fee." which is fine but doesn't tell the user what to do.
      if (err.code === 'insufficient_balance') {
        toast.error('Not enough wallet balance — top up first to join.');
      } else {
        toast.error(err.message || 'Could not join.');
      }
    }
  });
}

// ---- pre-roll ad ----------------------------------------------------------

function detectAdKind(url) {
  const u = String(url || '');
  // Any youtube.com subdomain (www., m., music.) or youtu.be short-link.
  if (/(?:^|\/\/)(?:[a-z0-9-]+\.)?youtube\.com\/|(?:^|\/\/)youtu\.be\//i.test(u)) return 'youtube';
  if (/vimeo\.com\//i.test(u)) return 'vimeo';
  return 'mp4';
}

function extractYouTubeId(url) {
  // Covers watch?v=, youtu.be/, /embed/, /shorts/, /live/, and the
  // `?feature=share&v=` variant where v= isn't the first query param.
  // Anything else -> null; the caller falls back to the raw URL.
  const patterns = [
    /[?&]v=([\w-]{6,20})/i, // watch?v= or ?feature=share&v=
    /youtu\.be\/([\w-]{6,20})/i,
    /youtube\.com\/embed\/([\w-]{6,20})/i,
    /youtube\.com\/shorts\/([\w-]{6,20})/i,
    /youtube\.com\/live\/([\w-]{6,20})/i,
  ];
  for (const re of patterns) {
    const m = String(url || '').match(re);
    if (m) return m[1];
  }
  return null;
}

function extractVimeoId(url) {
  const m = String(url || '').match(/vimeo\.com\/(?:video\/)?(\d{5,15})/i);
  return m ? m[1] : null;
}

function renderAdCard(live, ad) {
  const kind = detectAdKind(live.ad_video_url);
  let player = '';
  if (kind === 'youtube') {
    const id = extractYouTubeId(live.ad_video_url);
    // `mute=1` is required for the browser to allow autoplay on mobile.
    // `playsinline=1` keeps iOS from full-screening it. `origin=` is a
    // hard requirement for newer YouTube embed defenses — without it the
    // iframe will silently fail to load on some browsers (this is the
    // usual reason a raw youtu.be paste "just doesn't play").
    const origin = encodeURIComponent(window.location.origin);
    const src = id
      ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0&origin=${origin}`
      : String(live.ad_video_url);
    player = `<iframe id="ld-ad-video" src="${escape(src)}"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
                style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"></iframe>`;
  } else if (kind === 'vimeo') {
    const id = extractVimeoId(live.ad_video_url);
    const src = id
      ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&playsinline=1`
      : String(live.ad_video_url);
    player = `<iframe id="ld-ad-video" src="${escape(src)}"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowfullscreen
                style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"></iframe>`;
  } else {
    player = `<video id="ld-ad-video" src="${escape(String(live.ad_video_url))}"
                autoplay muted playsinline controls
                style="position:absolute;top:0;left:0;width:100%;height:100%;background:#000;"></video>`;
  }
  const skipLabel = ad.threshold > 0 ? `Skip in ${ad.threshold}s` : 'Skip →';
  return `
    <div class="card" id="ld-ad-card" style="margin-top:14px;padding:0;overflow:hidden;background:#000;border:1px solid #2A2244;">
      <div style="position:relative;width:100%;padding-top:56.25%;background:#000;">
        ${player}
      </div>
      <div class="row" style="padding:10px 12px;align-items:center;background:#0E0B1F;">
        <span class="muted" style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;">
          🎬 Sponsor message
        </span>
        <div class="sp"></div>
        <button type="button" id="ld-ad-skip"
                class="button-secondary button-inline"
                disabled
                style="padding:4px 12px;font-size:12px;font-weight:700;opacity:0.55;">
          ${escape(skipLabel)}
        </button>
      </div>
    </div>
  `;
}

function wireAdPlayer(ctx) {
  const ad = ctx.ad;
  if (!ad || !ad.needed || ad.completed) return;
  const skipBtn = document.getElementById('ld-ad-skip');
  const video = document.getElementById('ld-ad-video');
  if (!skipBtn) return;

  // Record the impression once per render pass. Server-side upsert makes
  // this idempotent even if we double-fire on a rapid re-render.
  if (!ad.startReported) {
    ad.startReported = true;
    void api(`/live/${ctx.liveId}/ad/view`, {
      method: 'POST',
      body: { completed: false },
    }).catch(() => { /* non-fatal — analytics only */ });
  }

  const finish = async () => {
    if (ad.completed) return;
    ad.completed = true;
    if (ctx.adTickTimer) { clearInterval(ctx.adTickTimer); ctx.adTickTimer = null; }
    try {
      await api(`/live/${ctx.liveId}/ad/view`, {
        method: 'POST',
        body: { completed: true },
      });
    } catch { /* non-fatal — analytics only */ }
    // Re-render so the Join CTA unlocks and the ad card unmounts.
    void loadAndRender(ctx);
  };

  // Native <video>: fire completion on `ended`. We don't wire an
  // equivalent postMessage bridge for YouTube/Vimeo — the skip button
  // handles the completion signal for those, which is enough for
  // analytics parity.
  if (video && video.tagName === 'VIDEO') {
    video.addEventListener('ended', () => { void finish(); }, { once: true });
  }

  // Countdown -> unlock the Skip button.
  let remaining = ad.threshold;
  const paint = () => {
    if (remaining > 0) {
      skipBtn.textContent = `Skip in ${remaining}s`;
      skipBtn.disabled = true;
      skipBtn.style.opacity = '0.55';
    } else {
      skipBtn.textContent = 'Skip →';
      skipBtn.disabled = false;
      skipBtn.style.opacity = '1';
    }
  };
  paint();
  if (remaining > 0) {
    ctx.adTickTimer = setInterval(() => {
      remaining = Math.max(0, remaining - 1);
      paint();
      if (remaining === 0 && ctx.adTickTimer) {
        clearInterval(ctx.adTickTimer);
        ctx.adTickTimer = null;
      }
    }, 1000);
  }

  skipBtn.addEventListener('click', () => { void finish(); });
}
